import {Evaluable, evaluateWhenFunction} from "../evaluable";
import {
    AssertionLogicFunction,
    assertType,
    NarrowedNonUnionTypeAssertController
} from "../type-assertion/type-assertion";

export type OverridableValueOriginalState<V> = {
    __original: true;
    value: V;
};

/** A value that can either be overridden with a custom value or use the original source value. */
export type OverridableValue<V> = V | OverridableValueOriginalState<V>;

export namespace OverridableValue {
    export type ResolveValueType<T> = T extends OverridableValue<infer U> ? U : T;

    export type ResolveOverridableValuesKeys<T> = {
        [K in keyof T]-?: OverridableValueOriginalState<any> extends T[K] ? K : never
    }[keyof T]

    export type ResolveNonOverridableValuesKeys<T> = {
        [K in keyof T]-?: OverridableValueOriginalState<any> extends T[K] ? never : K
    }[keyof T]
}

export function overridableValueIsOriginal<V>(value: any): value is OverridableValueOriginalState<V> {
    return typeof value === 'object' && value !== null && (value as OverridableValueOriginalState<V>).__original === true;
}

export function overridableValueIsOverridden<V>(value: OverridableValue<V>): value is V {
    return !overridableValueIsOriginal(value);
}

export function overridableValueConvert<V, R>(overridableValue: OverridableValue<V>, convertFunction: (isOverridden: boolean, value: V) => R): R {
    return convertFunction(overridableValueIsOverridden(overridableValue), overridableValueGetValue(overridableValue));
}

export function overridableValueGetValue<V>(value: OverridableValue<V>): V {
    if (overridableValueIsOriginal<V>(value)) {
        return value.value;
    } else {
        return value;
    }
}

export function overridableValueModifyValue<V>(
    overridableValue: OverridableValue<V>,
    modificationFunction: Evaluable<(value: V) => V>
): OverridableValue<V> {
    const value = overridableValueGetValue(overridableValue);

    const modifiedValue = evaluateWhenFunction(modificationFunction, value);

    if (overridableValueIsOriginal(overridableValue)) {
        return overridableValueCreateOriginal(modifiedValue);
    } else {
        return modifiedValue;
    }
}

export function overridableValueGetOverriddenValue<V, O>(value: OverridableValue<V>, originalValue: O): V | O {
    if (overridableValueIsOriginal<V>(value)) {
        return originalValue;
    } else {
        return value;
    }
}

export function overridableValueCreateOriginal<V>(value: V): OverridableValueOriginalState<V> {
    return {
        __original: true,
        value: value
    }
}

export function overridableValueCreate<V>(value: V, isOriginal: boolean) {
    if (isOriginal) {
        return overridableValueCreateOriginal(value);
    } else {
        return value;
    }
}

export function overridableValueEnsureOriginal<V>(value: OverridableValue<V>): OverridableValueOriginalState<V> {
    if (overridableValueIsOriginal(value)) {
        return value;
    } else {
        return overridableValueCreateOriginal(value);
    }
}

export function overridableValueToggleOriginal<V>(
    value: OverridableValue<V>,
    isOriginal: Evaluable<(isOriginal: boolean) => boolean>
): OverridableValue<V> {
    const wasOriginal = overridableValueIsOriginal(value);

    const evaluatedIsOriginal = evaluateWhenFunction(isOriginal, wasOriginal);

    if (wasOriginal !== evaluatedIsOriginal) {
        return wasOriginal ? value.value : overridableValueCreateOriginal(value);
    } else {
        return value;
    }
}

export function assertOverridableValue<V>(overridableValue: OverridableValue<V>, assertionLogic: AssertionLogicFunction<V>): asserts overridableValue is OverridableValue<V>;
export function assertOverridableValue<V>(overridableValue: unknown, assertionLogic: AssertionLogicFunction<V>): asserts overridableValue is OverridableValue<V>;
export function assertOverridableValue<V>(overridableValue: unknown, assertionLogic: AssertionLogicFunction<V>): asserts overridableValue is OverridableValue<V> {
    assertType<OverridableValue<V>>(overridableValue, assert => {
        (assert as any).isUnion(
            assertionLogic,
            (assert: NarrowedNonUnionTypeAssertController<OverridableValueOriginalState<V>>) => assert.isObject({
                __original: assert => assert.isOneOfValues([true]),
                value: assertionLogic
            })
        )
    })
}
